import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { Select } from "@/components/Select";
import { useApi } from "@/lib/api";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import type { MemberResource } from "./shared";
import { ResourceTable } from "./ResourceTable";
import type { ResourcesLayoutContext } from "./ResourcesLayout";

export function MemberResourceList({
  type,
  typeLabelSentence,
  typeLabelPlural,
  kindOptions,
}: {
  type: string;
  typeLabelSentence: string;
  typeLabelPlural: string;
  kindOptions: string[];
}) {
  const api = useApi();
  const { orgId } = useParams();
  const { setSummary } = useOutletContext<ResourcesLayoutContext>();
  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const filters = useMemo(() => ({ name, kind, page }), [name, kind, page]);
  const filter = useDebouncedValue(filters, 300);
  const params = new URLSearchParams({
    type,
    name: filter.name,
    kind: filter.kind,
    page: String(filter.page),
    pageSize: String(PAGE_SIZE),
  });
  const path = `/organizations/${orgId}/resources?${params}`;
  const [result, setResult] = useState<{
    path: string;
    data: PagedResult<MemberResource> | null;
  } | null>(null);
  const current = result?.path === path ? result : null;

  useEffect(() => {
    let cancelled = false;
    void api.get<PagedResult<MemberResource>>(path).then((data) => {
      if (!cancelled) setResult({ path, data });
    });
    return () => {
      cancelled = true;
    };
  }, [api, path, attempt]);

  useEffect(() => {
    setSummary(
      current?.data
        ? { total: current.data.totalCount, needsAttention: 0, typeLabelPlural }
        : null,
    );
    return () => setSummary(null);
  }, [current, setSummary, typeLabelPlural]);

  const hasFilters = Boolean(name || kind);
  function clearFilters() {
    setName("");
    setKind("");
    setPage(1);
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <SearchField
          value={name}
          onChange={(value) => {
            setName(value);
            setPage(1);
          }}
          label={`Search ${typeLabelPlural} by name`}
          placeholder={`Search ${typeLabelSentence} name`}
        />
        <Select
          aria-label="Filter by kind"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All kinds</option>
          {kindOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
      </div>
      {!current ? (
        <ResourceTable resources={null} />
      ) : !current.data ? (
        <LoadError
          what="your resources"
          onRetry={() => {
            setResult(null);
            setAttempt((value) => value + 1);
          }}
        />
      ) : !current.data.items.length ? (
        <EmptyState
          title={
            hasFilters
              ? `No ${typeLabelPlural} match these filters`
              : `No ${typeLabelPlural} yet`
          }
          description={
            hasFilters
              ? "Try a different name or kind."
              : "Your teams haven't granted access to any resources of this type. Contact your administrator if you need access."
          }
        />
      ) : (
        <>
          <ResourceTable resources={current.data.items} />
          <Pagination
            page={filter.page}
            totalPages={Math.ceil(current.data.totalCount / PAGE_SIZE)}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
